import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { Navigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { AdminGalleries } from "@/components/AdminGalleries";
import { AdminRegistrations } from "@/components/AdminRegistrations";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

const inputCls =
  "w-full bg-transparent border-0 border-b border-white/20 rounded-none px-0 py-3 text-[#F5F4F0] placeholder:text-[#525252] focus:outline-none focus:border-[#F5F4F0] transition-colors duration-300 text-sm";

const LoginForm = () => {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    const res = await login(email, password);
    if (!res.ok) setError(res.error);
    setBusy(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
      className="w-full max-w-md"
      data-testid="admin-login-card"
    >
      <img src="/photos/logo-trimmed.webp" alt="TapeShift Wedding" className="h-10 w-auto mb-10" data-testid="admin-logo" />
      <p className="text-xs tracking-[0.4em] uppercase text-[#525252] mb-4">Studio Access</p>
      <h1 className="font-serif font-light text-4xl tracking-tighter text-[#F5F4F0] mb-12">TapeShift <span className="font-accent italic text-[#C9A26B]">Studio</span></h1>
      <form onSubmit={submit} className="flex flex-col gap-8" data-testid="admin-login-form">
        <div>
          <label htmlFor="admin-email" className="text-[10px] tracking-[0.3em] uppercase text-[#525252]">Email</label>
          <input id="admin-email" type="email" required data-testid="admin-email-input" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin@tapeshiftwedding.com" className={inputCls} />
        </div>
        <div>
          <label htmlFor="admin-password" className="text-[10px] tracking-[0.3em] uppercase text-[#525252]">Password</label>
          <input id="admin-password" type="password" required data-testid="admin-password-input" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className={inputCls} />
        </div>
        {error && <p className="text-sm text-red-400" data-testid="admin-login-error">{error}</p>}
        <button type="submit" disabled={busy} data-testid="admin-login-submit"
          className="self-start text-xs tracking-[0.35em] uppercase bg-[#C9A26B] text-[#0A0A0A] px-10 py-4 hover:bg-transparent hover:text-[#C9A26B] border border-[#C9A26B] transition-colors duration-400 disabled:opacity-50">
          {busy ? "Signing in…" : "Sign In"}
        </button>
      </form>
    </motion.div>
  );
};

const Inquiries = () => {
  const { user, logout } = useAuth();
  const [items, setItems] = useState(null);
  const [tab, setTab] = useState("inquiries");

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("drive") === "connected") {
      toast.success("Google Drive connected — new photo uploads now go to your Drive");
      setTab("storage");
      window.history.replaceState({}, "", "/admin");
    }
  }, []);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/inquiries");
      setItems(data);
    } catch (e) {
      toast.error("Could not load inquiries");
      setItems([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const markRead = async (id) => {
    try {
      await api.patch(`/inquiries/${id}/read`);
      setItems((prev) => prev.map((i) => (i.id === id ? { ...i, status: "read" } : i)));
    } catch (e) {
      toast.error("Could not update inquiry");
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
      className="w-full max-w-6xl"
      data-testid="admin-dashboard"
    >
      <div className="flex items-end justify-between mb-8 flex-wrap gap-6">
        <div>
          <p className="text-xs tracking-[0.4em] uppercase text-[#525252] mb-4">Signed in as {user?.email}</p>
          <h1 className="font-serif font-light text-4xl md:text-5xl tracking-tighter text-[#F5F4F0]">Studio</h1>
        </div>
        <div className="flex gap-4">
          <button data-testid="admin-instagram-sync-btn" onClick={async () => {
            try {
              const { data } = await api.post("/instagram/sync");
              toast.success(`Instagram synced — ${data.synced} posts checked`);
            } catch (e) {
              toast.error(e.response?.data?.detail || "Instagram not configured yet");
            }
          }} className="text-xs tracking-[0.3em] uppercase border border-[#C9A26B]/50 px-5 py-3 text-[#C9A26B] hover:bg-[#C9A26B] hover:text-[#0A0A0A] transition-colors duration-300">Sync Instagram</button>
          <button data-testid="admin-refresh-btn" onClick={load} className="text-xs tracking-[0.3em] uppercase border border-white/30 px-5 py-3 text-[#F5F4F0] hover:bg-white/10 transition-colors duration-300">Refresh</button>
          <button data-testid="admin-logout-btn" onClick={logout} className="text-xs tracking-[0.3em] uppercase border border-white/30 px-5 py-3 text-[#A3A3A3] hover:text-[#F5F4F0] transition-colors duration-300">Log Out</button>
        </div>
      </div>

      <div className="flex gap-8 mb-10 border-b border-white/10" data-testid="admin-tabs">
        {[["inquiries", "Inquiries"], ["galleries", "Client Galleries"], ["registrations", "Registrations"], ["storage", "Storage"]].map(([key, label]) => (
          <button
            key={key}
            data-testid={`admin-tab-${key}`}
            onClick={() => setTab(key)}
            className={`pb-4 text-xs tracking-[0.3em] uppercase transition-colors duration-300 border-b-2 -mb-px ${
              tab === key ? "text-[#C9A26B] border-[#C9A26B]" : "text-[#737373] border-transparent hover:text-[#F5F4F0]"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "galleries" && <AdminGalleries />}

      {tab === "registrations" && <AdminRegistrations />}

      {tab === "storage" && <AdminStorage />}

      {tab === "inquiries" && (items === null ? (
        <p className="text-[#525252]" data-testid="inquiries-loading">Loading…</p>
      ) : items.length === 0 ? (
        <p className="text-[#A3A3A3]" data-testid="inquiries-empty">No inquiries yet. New booking form submissions will appear here.</p>
      ) : (
        <div className="border border-white/10" data-testid="inquiries-table">
          <Table>
            <TableHeader>
              <TableRow className="border-white/10 hover:bg-transparent">
                <TableHead className="text-[#525252] uppercase tracking-[0.2em] text-[10px]">Received</TableHead>
                <TableHead className="text-[#525252] uppercase tracking-[0.2em] text-[10px]">Couple</TableHead>
                <TableHead className="text-[#525252] uppercase tracking-[0.2em] text-[10px]">Email</TableHead>
                <TableHead className="text-[#525252] uppercase tracking-[0.2em] text-[10px]">Date</TableHead>
                <TableHead className="text-[#525252] uppercase tracking-[0.2em] text-[10px]">Venue</TableHead>
                <TableHead className="text-[#525252] uppercase tracking-[0.2em] text-[10px]">WhatsApp</TableHead>
                <TableHead className="text-[#525252] uppercase tracking-[0.2em] text-[10px]">Story</TableHead>
                <TableHead className="text-[#525252] uppercase tracking-[0.2em] text-[10px]">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((i) => (
                <TableRow key={i.id} className="border-white/10 hover:bg-white/5" data-testid={`inquiry-row-${i.id}`}>
                  <TableCell className="text-[#A3A3A3] text-xs whitespace-nowrap">{new Date(i.created_at).toLocaleDateString()}</TableCell>
                  <TableCell className="text-[#F5F4F0] text-sm">{i.names}</TableCell>
                  <TableCell className="text-[#A3A3A3] text-sm">{i.email}</TableCell>
                  <TableCell className="text-[#A3A3A3] text-sm whitespace-nowrap">{i.wedding_date || "—"}</TableCell>
                  <TableCell className="text-[#A3A3A3] text-sm">{i.venue || "—"}</TableCell>
                  <TableCell className="text-[#A3A3A3] text-sm">
                    {i.whatsapp ? (
                      <a
                        href={`https://wa.me/${i.whatsapp.replace(/\D/g, "")}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`whatsapp-link-${i.id}`}
                        className="text-[#C9A26B] hover:underline"
                      >
                        {i.whatsapp}
                      </a>
                    ) : "—"}
                  </TableCell>
                  <TableCell className="text-[#A3A3A3] text-sm max-w-[220px] truncate">{i.message || "—"}</TableCell>
                  <TableCell>
                    {i.status === "new" ? (
                      <button data-testid={`mark-read-${i.id}`} onClick={() => markRead(i.id)} className="text-[10px] tracking-[0.2em] uppercase border border-white/30 px-3 py-1.5 text-[#F5F4F0] hover:bg-[#F5F4F0] hover:text-[#0A0A0A] transition-colors duration-300 whitespace-nowrap">
                        Mark read
                      </button>
                    ) : (
                      <span className="text-[10px] tracking-[0.2em] uppercase text-[#525252]">Read</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ))}
    </motion.div>
  );
};

const formatBytes = (n) => {
  if (!n) return "0 MB";
  if (n >= 1099511627776) return `${(n / 1099511627776).toFixed(2)} TB`;
  if (n >= 1073741824) return `${(n / 1073741824).toFixed(2)} GB`;
  if (n >= 1048576) return `${(n / 1048576).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(n / 1024))} KB`;
};

const DriveCard = () => {
  const [status, setStatus] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/drive/status");
      setStatus(data);
    } catch (e) {
      setStatus({ connected: false });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const connect = async () => {
    try {
      const { data } = await api.get("/drive/connect");
      window.open(data.authorization_url, "_blank", "noopener");
      toast.info("Finish signing in with Google in the new tab — it brings you back here when done");
    } catch (err) {
      toast.error("Could not start Google Drive connection");
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect Google Drive? New uploads will use the built-in 5 GB storage again. Photos already in Drive keep working.")) return;
    try {
      await api.post("/drive/disconnect");
      toast.success("Google Drive disconnected");
      load();
    } catch (err) {
      toast.error("Could not disconnect");
    }
  };

  if (status === null) return null;

  const usedPct = status.limit_bytes ? Math.min(100, (status.used_bytes / status.limit_bytes) * 100) : 0;

  return (
    <div className="border border-white/10 p-8 mb-10" data-testid="drive-card">
      {status.connected ? (
        <div className="flex flex-wrap items-center justify-between gap-6">
          <div className="flex-1 min-w-[240px]">
            <p className="text-[10px] tracking-[0.3em] uppercase text-[#525252] mb-2">Google Drive</p>
            <p className="text-sm text-[#F5F4F0]" data-testid="drive-email">Connected{status.email ? ` — ${status.email}` : ""}</p>
            {status.limit_bytes > 0 && (
              <>
                <div className="h-1 bg-white/10 mt-4 max-w-md">
                  <div className="h-1 bg-[#C9A26B]" style={{ width: `${Math.max(1, usedPct)}%` }} />
                </div>
                <p className="text-[10px] tracking-[0.2em] uppercase text-[#737373] mt-2" data-testid="drive-usage">
                  {formatBytes(status.used_bytes)} of {formatBytes(status.limit_bytes)} used — new uploads go to your Drive
                </p>
              </>
            )}
            {status.error && <p className="text-xs text-red-400 mt-2">{status.error}</p>}
          </div>
          <button onClick={disconnect} data-testid="drive-disconnect-btn"
            className="text-[10px] tracking-[0.2em] uppercase border border-white/10 px-4 py-2.5 text-[#737373] hover:text-red-400 hover:border-red-400/40 transition-colors duration-300">
            Disconnect
          </button>
        </div>
      ) : (
        <div className="flex flex-wrap items-center justify-between gap-6">
          <div>
            <p className="text-[10px] tracking-[0.3em] uppercase text-[#525252] mb-2">Google Drive</p>
            <p className="text-sm text-[#A3A3A3] max-w-xl">
              Not connected. Connect your Drive and every new photo upload goes straight to your own Google storage —
              deleting a photo in Studio really frees the space.
            </p>
          </div>
          <button onClick={connect} data-testid="drive-connect-btn"
            className="text-[10px] tracking-[0.2em] uppercase border border-[#C9A26B]/60 px-5 py-3 text-[#C9A26B] hover:bg-[#C9A26B] hover:text-[#0A0A0A] transition-colors duration-300">
            Connect Google Drive
          </button>
        </div>
      )}
    </div>
  );
};

const AdminStorage = () => {
  const [stats, setStats] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/storage");
      setStats(data);
    } catch (e) {
      toast.error("Could not load storage");
      setStats({ total_photos: 0, total_bytes: 0, galleries: [] });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (stats === null) return <p className="text-[#525252]" data-testid="storage-loading">Loading…</p>;

  return (
    <div data-testid="storage-panel">
      <DriveCard />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10 border border-white/10 mb-10">
        <div className="bg-[#0A0A0A] p-8">
          <p className="text-[10px] tracking-[0.3em] uppercase text-[#525252] mb-3">Total storage used</p>
          <p className="font-serif font-light text-4xl text-[#F5F4F0]" data-testid="storage-total-bytes">{formatBytes(stats.total_bytes)}</p>
        </div>
        <div className="bg-[#0A0A0A] p-8">
          <p className="text-[10px] tracking-[0.3em] uppercase text-[#525252] mb-3">Photos stored</p>
          <p className="font-serif font-light text-4xl text-[#F5F4F0]" data-testid="storage-total-photos">{stats.total_photos}</p>
        </div>
        <div className="bg-[#0A0A0A] p-8">
          <p className="text-[10px] tracking-[0.3em] uppercase text-[#525252] mb-3">Galleries using storage</p>
          <p className="font-serif font-light text-4xl text-[#F5F4F0]" data-testid="storage-gallery-count">{stats.galleries.length}</p>
        </div>
      </div>
      {stats.galleries.length === 0 ? (
        <p className="text-[#A3A3A3]" data-testid="storage-empty">No photos uploaded yet.</p>
      ) : (
        <div className="border border-white/10" data-testid="storage-table">
          <Table>
            <TableHeader>
              <TableRow className="border-white/10 hover:bg-transparent">
                <TableHead className="text-[#525252] uppercase tracking-[0.2em] text-[10px]">Gallery</TableHead>
                <TableHead className="text-[#525252] uppercase tracking-[0.2em] text-[10px]">Code</TableHead>
                <TableHead className="text-[#525252] uppercase tracking-[0.2em] text-[10px]">Photos</TableHead>
                <TableHead className="text-[#525252] uppercase tracking-[0.2em] text-[10px]">Storage</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {stats.galleries.map((g) => (
                <TableRow key={g.gallery_id} className="border-white/10 hover:bg-white/5" data-testid={`storage-row-${g.code}`}>
                  <TableCell className="text-[#F5F4F0] text-sm">{g.couple_name}</TableCell>
                  <TableCell className="text-[#A3A3A3] text-sm">{g.code}</TableCell>
                  <TableCell className="text-[#A3A3A3] text-sm">{g.photos}</TableCell>
                  <TableCell className="text-[#C9A26B] text-sm">{formatBytes(g.bytes)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};

export default function Admin() {
  const { user } = useAuth();

  if (user && user.role !== "admin") {
    return <Navigate to="/gallery" replace />;
  }

  return (
    <main className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-6 py-24">
      {user === null ? (
        <p className="text-[#525252]" data-testid="admin-loading">Loading…</p>
      ) : user === false ? (
        <LoginForm />
      ) : (
        <Inquiries />
      )}
    </main>
  );
}
